import { defineStore } from 'pinia'
import AgendaService from '@/services/agenda/AgendaService'
import DateManager from '@/services/dateManager'
import { devLog, devError, devWarn } from '@/services/logger'

import type { AgendaEvent, AgendaFilters, AgendaKind, CreateTaskData, Task, TaskNote, TaskTipo } from '@/types/agenda'

export type { AgendaEvent, AgendaKind, CreateTaskData, Task, TaskNote, TaskTipo }

export interface AgendaState {
  modo: 'mes' | 'semana' | 'dia'
  anchor: Date
  filters: AgendaFilters
  loading: boolean
  events: AgendaEvent[]
  page: number
  pageSize: number
  count: number
  selected: AgendaEvent | null
  responsaveis: Array<{ id: number; fullname: string }>
  selectedNotes: Array<TaskNote>
  form: {
    rescheduleDate: any
    rescheduleTime: string | Date | null
    reopenDate: any
    reopenTime: string | Date | null
    cancelReason: string
    noteText: string
  }
}

function mergeDateTime(date: Date, time: string | Date | null): string {
  // Usar o novo método do DateManager que converte corretamente para UTC
  return DateManager.utils.formatDateTimeForAPI(date, time || undefined)
}

export const useAgendaStore = defineStore('agenda', {
  state: (): AgendaState => ({
    modo: 'mes',
    anchor: new Date(),
    filters: {
      start: '',
      end: '',
      me: true,
      kind: 'all',
      search: '',
      page_size: 100,
    },
    loading: false,
    events: [],
    page: 1,
    pageSize: 100,
    count: 0,
    selected: null,
    responsaveis: [],
    selectedNotes: [],
    form: {
      rescheduleDate: null,
      rescheduleTime: null,
      reopenDate: null,
      reopenTime: null,
      cancelReason: '',
      noteText: '',
    },
  }),

  getters: {
    periodLabel: (state) => {
      const d = state.anchor
      return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    },

    // Métricas simples dos eventos
    metrics: (state) => {
      const m = { scheduled: 0, executed: 0, canceled: 0, no_show: 0 }
      for (const e of state.events) {
        ;(m as any)[e.kind]++
      }
      return m
    },
  },

  actions: {
    // ===== NAVEGAÇÃO TEMPORAL =====

    setModo(m: 'mes' | 'semana' | 'dia') {
      this.modo = m
    },

    goToday() {
      this.anchor = new Date()
      this.computePeriod()
      this.fetchAgenda()
    },

    goPrev() {
      const d = new Date(this.anchor)
      if (this.modo === 'mes') d.setMonth(d.getMonth() - 1)
      if (this.modo === 'semana') d.setDate(d.getDate() - 7)
      if (this.modo === 'dia') d.setDate(d.getDate() - 1)
      this.anchor = d
      this.computePeriod()
      this.fetchAgenda()
    },

    goNext() {
      const d = new Date(this.anchor)
      if (this.modo === 'mes') d.setMonth(d.getMonth() + 1)
      if (this.modo === 'semana') d.setDate(d.getDate() + 7)
      if (this.modo === 'dia') d.setDate(d.getDate() + 1)
      this.anchor = d
      this.computePeriod()
      this.fetchAgenda()
    },

    computePeriod() {
      const start = new Date(this.anchor)
      const end = new Date(this.anchor)

      if (this.modo === 'mes') {
        start.setDate(1)
        start.setHours(0, 0, 0, 0)
        end.setMonth(end.getMonth() + 1, 0)
        end.setHours(23, 59, 59, 999)
      } else if (this.modo === 'semana') {
        const dow = start.getDay() // 0-6 (dom-sab)
        start.setDate(start.getDate() - dow)
        start.setHours(0, 0, 0, 0)
        end.setDate(start.getDate() + 6)
        end.setHours(23, 59, 59, 999)
      } else {
        // dia
        start.setHours(0, 0, 0, 0)
        end.setHours(23, 59, 59, 999)
      }

      this.filters.start = start.toISOString()
      this.filters.end = end.toISOString()
    },

    // ===== CARREGAMENTO DE DADOS =====

    async fetchAgenda() {
      this.loading = true
      try {
        const response = await AgendaService.getAgenda(this.filters)
        this.events = response.data.results || []
        this.count = response.data.count || this.events.length
      } catch (error) {
        devError('Erro ao carregar agenda:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async loadResponsaveis() {
      try {
        const response = await AgendaService.getResponsaveis()
        this.responsaveis = response.data.map((user: any) => ({
          id: user.id,
          fullname: user.user_details.fullname,
        }))
      } catch (error) {
        devError('Erro ao carregar responsáveis:', error)
      }
    },

    async fetchNotes(taskId: string) {
      try {
        const response = await AgendaService.getTarefa(taskId)

        const notas = (response.data.notas || []).map((note: TaskNote) => {
          // Regex melhorada para capturar diferentes formatos de data ISO
          const dateRegex = /\b(\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d{3,6})?(?:Z|[+-]\d{2}:\d{2})?)\b/g

          let txt = note.texto

          // Substituir todas as ocorrências de datas no texto
          txt = txt.replace(dateRegex, (match) => {
            try {
              const date = new Date(match)
              // Verificar se a data é válida
              if (!isNaN(date.getTime())) {
                return DateManager.formatToBR(date, 'dd/MM/yy HH:mm')
              }
              return match // Retornar o texto original se a data for inválida
            } catch (error) {
              devWarn('Erro ao processar data:', match, error)
              return match // Retornar o texto original em caso de erro
            }
          })

          return {
            ...note,
            texto: txt,
          }
        })

        this.selectedNotes = [...notas]
      } catch (error) {
        devError('Erro ao carregar notas:', error)
      }
    },

    // ===== AÇÕES DE TAREFAS =====

    async reschedule(taskId: string) {
      if (!this.form.rescheduleDate) {
        throw new Error('Data de reagendamento é obrigatória')
      }

      const iso = mergeDateTime(this.form.rescheduleDate, this.form.rescheduleTime)
      await AgendaService.rescheduleTask(taskId, {
        quando: iso,
        reason: 'Reagendamento via agenda',
      })
      await this.fetchAgenda()

      // Limpar formulário
      this.form.rescheduleDate = null
      this.form.rescheduleTime = null
    },

    async reopen(taskId: string) {
      if (!this.form.reopenDate) {
        throw new Error('Data de reabertura é obrigatória')
      }

      const iso = mergeDateTime(this.form.reopenDate, this.form.reopenTime)
      await AgendaService.reopenTask(taskId, {
        quando: iso,
        reason: 'Reabertura via agenda',
      })
      await this.fetchAgenda()

      // Limpar formulário
      this.form.reopenDate = null
      this.form.reopenTime = null
    },

    async complete(taskId: string) {
      await AgendaService.completeTask(taskId)
      await this.fetchAgenda()
    },

    async cancel(taskId: string) {
      await AgendaService.cancelTask(taskId, {
        reason: this.form.cancelReason || 'Cancelado via agenda',
      })
      await this.fetchAgenda()

      // Limpar formulário
      this.form.cancelReason = ''
    },

    async noShow(taskId: string) {
      await AgendaService.noShowTask(taskId, {
        reason: 'No-show via agenda',
      })
      await this.fetchAgenda()
    },

    async addNote(taskId: string) {
      if (!this.form.noteText) {
        throw new Error('Texto da nota é obrigatório')
      }

      await AgendaService.addNoteToTask(taskId, {
        texto: this.form.noteText,
      })

      // Limpar formulário
      this.form.noteText = ''
    },

    // ===== CRIAR TAREFA =====

    async createTask(taskData: CreateTaskData) {
      try {
        this.loading = true

        // Validar e limpar dados antes de enviar
        const cleanTaskData: CreateTaskData = {
          titulo: taskData.titulo,
          tipo: taskData.tipo,
          quando: taskData.quando,
          responsavel: taskData.responsavel,
        }

        // Adicionar campos opcionais apenas se fornecidos
        if (taskData.contato && taskData.contato.trim()) {
          cleanTaskData.contato = taskData.contato.trim()
        }

        if (taskData.lead && taskData.lead > 0) {
          cleanTaskData.lead = taskData.lead
        }

        if (taskData.empresa_uuid && taskData.empresa_uuid.trim()) {
          cleanTaskData.empresa_uuid = taskData.empresa_uuid.trim()
        }

        devLog('📤 Enviando dados para criação de tarefa (keys):', Object.keys(cleanTaskData))

        await AgendaService.createTarefa(cleanTaskData)
        await this.fetchAgenda()
        return true
      } catch (error) {
        devError('❌ Erro ao criar tarefa:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    // ===== UTILITÁRIOS =====

    resetFilters() {
      this.filters = {
        start: this.filters.start, // manter período atual
        end: this.filters.end, // manter período atual
        me: true,
        kind: 'all',
        search: '',
        page_size: 100,
      }
    },

    setSelected(event: AgendaEvent | null) {
      this.selected = event
    },
  },
})
